import type { HTMLAttributes } from 'react';

/**
 * Primitive de carte/panneau (audit UX/UI passe 2, B1 et B3).
 *
 * ### Ce que la refonte éditoriale y change
 *
 * La version précédente ne connaissait qu'une seule forme : `rounded-card
 * border border-border`. Une bordure, toujours, autour de tout. Sur la fiche
 * recette cela donnait sept rectangles empilés de haut en bas — bandeau de
 * visibilité, en-tête, note personnelle, rangement, historique — dont aucun
 * n'était plus important qu'un autre, et dont le seul effet collectif était
 * de faire ressembler la page à un formulaire d'administration.
 *
 * Trois tons désormais, parce qu'un panneau a trois raisons d'exister :
 *
 * | ton       | ce qu'il dit | rendu |
 * | --------- | ------------ | ----- |
 * | `plain`   | « ceci est un objet posé sur la page » | surface + filet |
 * | `sunken`  | « ceci est un creux, un contenant » | fond en creux, pas de filet |
 * | `outline` | « ceci est un cadre, il ne contient rien encore » | filet seul, pas de fond |
 *
 * Et l'élévation reste séparée du ton : une carte de recette est `plain` +
 * `hover`, un bandeau de minuteurs est `plain` + `raised`. Le §6 de la
 * mission met en garde autant contre les ombres trop fortes que contre les
 * bordures systématiques — d'où deux réglages plutôt qu'un.
 */
export type CardElevation = 'none' | 'hover' | 'raised';
export type CardTone = 'plain' | 'sunken' | 'outline';

const TONE_CLASSES: Record<CardTone, string> = {
  plain: 'border border-border',
  sunken: 'bg-surface-sunken',
  outline: 'border border-border',
};

export interface CardClassesOptions {
  /**
   * `p-4` par défaut (et non `p-3` comme avant) : à 12 px, le texte touchait
   * presque le filet et la carte se lisait comme une case de tableau.
   * Désactivable pour un conteneur qui gère son propre espacement — carte de
   * recette avec photo à fleur de bord, par exemple.
   */
  padded?: boolean;
  /** Fond `bg-surface` explicite — nécessaire quand la carte doit se détacher du fond de page ; omis par défaut pour les panneaux qui vivent déjà sur une surface. */
  surface?: boolean;
  tone?: CardTone;
  elevation?: CardElevation;
  className?: string;
}

export function cardClasses({
  padded = true,
  surface = false,
  tone = 'plain',
  elevation = 'none',
  className = '',
}: CardClassesOptions = {}): string {
  // `sunken` porte déjà son fond : lui superposer `bg-surface` l'annulerait.
  const surfaceClass = surface && tone !== 'sunken' ? 'bg-surface' : '';
  const paddingClass = padded ? 'p-4' : '';
  const elevationClass =
    elevation === 'raised'
      ? 'shadow-raised'
      : elevation === 'hover'
        ? 'transition-shadow duration-(--duration-quick) ease-entrance hover:shadow-raised'
        : '';
  return `rounded-card ${TONE_CLASSES[tone]} ${surfaceClass} ${paddingClass} ${elevationClass} ${className}`
    .replace(/\s+/g, ' ')
    .trim();
}

export interface CardProps extends HTMLAttributes<HTMLDivElement>, CardClassesOptions {}

export function Card({
  padded = true,
  surface = false,
  tone = 'plain',
  elevation = 'none',
  className = '',
  ...props
}: CardProps): React.JSX.Element {
  return <div className={cardClasses({ padded, surface, tone, elevation, className })} {...props} />;
}
